import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { TvGuideDetail } from './Detail/TvGuideDetail';
import { TvGuideGrid } from './Grid/TvGuideGrid';
import { TvGuideList } from './List/TvGuideList';
import { enrichWithTabIndex, getInitialTime } from './tv-guide-helper';
import { defaultPixelsPerMinute, defaultPlaceholderChannelCount, defaultTimelineIntervalInMin } from './tv-guide-types';
import { TvGuideMessage } from './TvGuideMessage';
import { centralEuropeanNow } from '../../../../play-core/utils/date-helper';
import { useAnalyticsDispatch } from '../../analytics/AnalyticsContext';
import { useTvGuide } from '../../hooks/use-tv-guide';
import { useViewport } from '../../providers/ViewportProvider/ViewportContext';
import { isoDate } from '../../utils/date-formatter';
import { useBottomDialogDispatch } from '../BottomDialog/BottomDialogContext';
import { AriaTimePicker } from '../DatePicker/AriaTimePicker';
import { useLoadingBar } from '../LoadingBar/LoadingBarProvider';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const TvGuide = ({
  activeChannelId,
  date = isoDate(centralEuropeanNow()),
  pixelsPerMinute = defaultPixelsPerMinute,
  timelineIntervalInMin = defaultTimelineIntervalInMin,
  channelCount = defaultPlaceholderChannelCount,
  initialData,
  providers,
  analyticsPageId,
  stickyTopPosition
}) => {
  const [time, setTime] = useState(getInitialTime(date));
  const {
    isSmall
  } = useViewport();
  const [tvGuideType, setTvGuideType] = useState();
  const dispatch = useBottomDialogDispatch();

  /*
  The first render output on the server needs to be identical to the first render on the client.
  So we initialize tvGuideType with undefined and set the correct tvGuideType asynchronously.
  See also https://reactjs.org/docs/react-dom.html#hydrate.
   */
  useEffect(() => {
    setTvGuideType(isSmall ? 'List' : 'Grid');
  }, [isSmall]);
  useEffect(() => {
    setTime(getInitialTime(date));
  }, [date]);
  const {
    programs,
    isLoading,
    isError
  } = useTvGuide(date, providers, initialData);
  useLoadingBar(isLoading);
  const isEmpty = (programs?.length === 0 || programs?.every(day => day.programList.length === 0)) ?? false;
  const showMessage = isEmpty || isError;
  const newPrograms = enrichWithTabIndex(programs, time, date);
  const placeholderChannelCount = channelCount - (programs?.length ?? 0);
  const analytics = useAnalyticsDispatch();
  const onSelectProgram = useCallback(selectedProgram => {
    if (tvGuideType) {
      analytics({
        type: 'click-event',
        name: 'TvGuideOpenInfoBox',
        value1: analyticsPageId,
        value2: tvGuideType,
        value3: selectedProgram.program.title,
        value4: selectedProgram.program.mediaUrn
      });
    }
    dispatch({
      type: 'show-dialog',
      dialog: /*#__PURE__*/_jsx(TvGuideDetail, {
        selectedProgram: selectedProgram,
        date: date,
        analyticsPageId: analyticsPageId
      })
    });
  }, [tvGuideType, dispatch, date, analyticsPageId, analytics]);
  const getTvGuideLayout = () => {
    switch (tvGuideType) {
      case 'List':
        return /*#__PURE__*/_jsx(TvGuideList, {
          time: time,
          date: date,
          programs: newPrograms,
          activeChannelId: activeChannelId,
          placeholderChannelCount: placeholderChannelCount,
          isLoading: isLoading,
          onSelectProgram: onSelectProgram,
          stickyTopPosition: stickyTopPosition
        });
      default:
        return /*#__PURE__*/_jsx(TvGuideGrid, {
          time: time,
          date: date,
          programs: newPrograms,
          activeChannelId: activeChannelId,
          pixelsPerMinute: pixelsPerMinute,
          placeholderChannelCount: placeholderChannelCount,
          timelineIntervalInMin: timelineIntervalInMin,
          isLoading: isLoading,
          onSelectProgram: onSelectProgram,
          analyticsPageId: analyticsPageId,
          stickyTopPosition: stickyTopPosition
        });
    }
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(AriaTimePicker, {
      time: time,
      onChange: setTime
    }), showMessage ? /*#__PURE__*/_jsx(TvGuideMessage, {
      isLoading: isLoading,
      isError: isError,
      isEmpty: isEmpty
    }) : getTvGuideLayout()]
  });
};